import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getInvoices, submitInvoiceProof } from '../../services/api';

// Lê um File como data URL base64. Usado no upload do comprovante PIX.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Falha ao ler o arquivo'));
    reader.readAsDataURL(file);
  });
}

const PROOF_ACCEPT = 'application/pdf,image/png,image/jpeg,image/jpg,image/webp';
const PROOF_MAX_MB = 2;

const S_BADGE = { pending:'badge-warning', paid:'badge-success', overdue:'badge-danger', cancelled:'badge-gray' };
const S_LABEL = { pending:'Pendente', paid:'Pago', overdue:'Vencido', cancelled:'Cancelado' };
const fmt = n => `R$ ${parseFloat(n||0).toLocaleString('pt-BR',{minimumFractionDigits:2})}`;

// Safe date formatter: handles YYYY-MM-DD strings AND ISO strings from DB
const fmtDate = (d) => {
  if (!d) return '';
  const s = typeof d === 'string' && d.length > 10 ? d.slice(0, 10) : typeof d === 'string' ? d : new Date(d).toISOString().slice(0, 10);
  return new Date(s + 'T12:00:00').toLocaleDateString('pt-BR');
};

// A copia-e-cola PIX payload always starts with '000201'
const isPixPayload = code => code?.startsWith('000201');

function PixQRCode({ code }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(code)}`;
  return (
    <div style={{ textAlign:'center', margin:'16px 0' }}>
      {!imgFailed ? (
        <img src={url} alt="QR Code PIX" width={180} height={180}
          style={{ borderRadius:8, border:'1px solid var(--gray-200)', display:'block', margin:'0 auto' }}
          onError={() => setImgFailed(true)} />
      ) : (
        <div style={{ width:180, height:180, margin:'0 auto', borderRadius:8, border:'1px solid var(--gray-200)', background:'var(--gray-50)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'var(--gray-500)', textAlign:'center', padding:8 }}>
          QR Code indisponível.<br />Use o código PIX abaixo.
        </div>
      )}
      <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:6 }}>Escaneie com o app do seu banco</div>
    </div>
  );
}

export default function TenantBilling() {
  const [invoices, setInvoices]  = useState([]);
  const [summary,  setSummary]   = useState({});
  const [loading,  setLoading]   = useState(true);
  const [pixModal, setPixModal]  = useState(null);
  const [statusF,  setStatusF]   = useState('');
  const [copied,   setCopied]    = useState(false);
  // Fluxo de comprovante: invoice alvo, arquivo selecionado, estado de envio
  const [proofModal, setProofModal] = useState(null); // invoice
  const [proofFile,  setProofFile]  = useState(null);
  const [proofSending, setProofSending] = useState(false);
  const [proofResult,  setProofResult]  = useState(null); // resposta do backend

  const load = useCallback(() => {
    setLoading(true);
    getInvoices({ status: statusF })
      .then(r => { setInvoices(r.data.invoices); setSummary(r.data.summary||{}); })
      .catch(() => toast.error('Erro ao carregar faturas'))
      .finally(() => setLoading(false));
  }, [statusF]);

  useEffect(() => { load(); }, [load]);

  const handleCopyPix = () => {
    if (!pixModal?.pix_code) return;
    navigator.clipboard.writeText(pixModal.pix_code).then(() => {
      setCopied(true);
      const msg = isPixPayload(pixModal.pix_code) ? 'Código PIX copiado!' : 'Chave PIX copiada!';
      toast.success(msg);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const openProofModal = (invoice) => {
    setProofModal(invoice);
    setProofFile(null);
    setProofResult(null);
  };

  const closeProofModal = () => {
    setProofModal(null);
    setProofFile(null);
    setProofResult(null);
  };

  const handleProofFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > PROOF_MAX_MB * 1024 * 1024) {
      toast.error(`Arquivo grande demais (${(f.size / 1024 / 1024).toFixed(1)} MB). Máximo ${PROOF_MAX_MB} MB.`);
      return;
    }
    const ALLOWED = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!ALLOWED.includes(f.type)) {
      toast.error('Formato não suportado. Use PDF ou imagem PNG/JPEG.');
      return;
    }
    setProofFile(f);
    setProofResult(null);
  };

  const handleSubmitProof = async () => {
    if (!proofModal || !proofFile) return;
    setProofSending(true);
    try {
      const dataUrl = await fileToDataUrl(proofFile);
      const r = await submitInvoiceProof(proofModal.id, dataUrl);
      setProofResult(r.data);
      if (r.data.auto_confirmed) {
        toast.success('Comprovante validado e fatura paga!');
      } else {
        toast.success('Comprovante enviado para revisão.');
      }
      // Recarrega a lista pra refletir novo status
      load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Falha ao enviar comprovante';
      toast.error(msg);
    } finally {
      setProofSending(false);
    }
  };

  const nextPending = invoices.find(i => i.status === 'pending');

  return (
    <div className="page-body">
      <div className="page-header"><h2>Faturamento</h2><p>Histórico de faturas e pagamentos</p></div>

      {nextPending && (
        <div className="warning-box" style={{ borderRadius:10, marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:14 }}>⏰ Fatura com vencimento próximo</div>
            <div style={{ fontSize:13, marginTop:2, opacity:0.8 }}>
              {fmt(nextPending.amount)} · Vence {fmtDate(nextPending.due_date)}
            </div>
          </div>
          {nextPending.pix_code && (
            <button className="btn btn-primary btn-sm" onClick={() => { setPixModal(nextPending); setCopied(false); }}>
              {isPixPayload(nextPending.pix_code) ? 'Pagar PIX' : 'Ver chave PIX'}
            </button>
          )}
        </div>
      )}

      <div className="stats-grid" style={{ marginBottom:20 }}>
        {[
          { icon:'⏳', label:'Pendente', value:fmt(summary.pending),  color:'amber' },
          { icon:'✅', label:'Pago',     value:fmt(summary.paid),     color:'green' },
          { icon:'⚠️', label:'Vencido',  value:fmt(summary.overdue),  color:'red'   },
        ].map(c => (
          <div key={c.label} className="stat-card">
            <div className={`stat-icon ${c.color}`}>{c.icon}</div>
            <div><div className="stat-label">{c.label}</div><div className="stat-value" style={{fontSize:17}}>{c.value}</div></div>
          </div>
        ))}
      </div>

      <div className="filter-bar">
        <select value={statusF} onChange={e => setStatusF(e.target.value)}>
          <option value="">Todas</option>
          <option value="pending">Pendentes</option>
          <option value="paid">Pagas</option>
          <option value="overdue">Vencidas</option>
          {/* Canceladas não aparecem pro tenant (filtro removido também no backend) */}
        </select>
      </div>

      {loading ? <div className="spinner" style={{margin:'40px auto',display:'block'}} /> : (
        invoices.length === 0
          ? (
            <div className="empty-state">
              <div className="empty-icon">💰</div>
              <h3>Nenhuma fatura disponível</h3>
              <p>Suas faturas aparecem aqui 5 dias antes do vencimento.</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {invoices.map(inv => (
                <div key={inv.id} className="mobile-card" style={inv.status==='overdue'?{borderLeft:'3px solid var(--danger)'}:{}}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div>
                      <div style={{ fontSize:12, color:'var(--gray-400)' }}>#{inv.id}</div>
                      <div style={{ fontWeight:700, fontSize:15, marginTop:2 }}>{inv.description || 'Fatura de serviço'}</div>
                      {inv.reference_month && <div style={{ fontSize:12, color:'var(--gray-500)' }}>Ref: {inv.reference_month}</div>}
                    </div>
                    <span className={`badge ${S_BADGE[inv.status]||'badge-gray'}`}>{S_LABEL[inv.status]||inv.status}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12 }}>
                    <div>
                      <div style={{ fontSize:22, fontWeight:800, color:'var(--gray-900)' }}>{fmt(inv.amount)}</div>
                      <div style={{ fontSize:12, color:'var(--gray-400)', marginTop:2 }}>
                        Vence: {fmtDate(inv.due_date)}
                        {inv.paid_at && <span style={{ marginLeft:8 }}>• Pago: {fmtDate(inv.paid_at)}</span>}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
                      {inv.pix_code && (inv.status==='pending' || inv.status==='overdue') && !inv.proof_status && (
                        <button className="btn btn-primary btn-sm" onClick={() => { setPixModal(inv); setCopied(false); }}>
                          {isPixPayload(inv.pix_code) ? 'Pagar PIX' : 'Ver chave PIX'}
                        </button>
                      )}
                      {(inv.status==='pending' || inv.status==='overdue') && inv.proof_status !== 'pending_review' && inv.proof_status !== 'auto_confirmed' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => openProofModal(inv)}>
                          📎 Já paguei
                        </button>
                      )}
                      {inv.proof_status === 'pending_review' && (
                        <span style={{ fontSize:12, color:'var(--warning)', fontWeight:700, padding:'6px 10px', background:'rgba(245,158,11,0.08)', borderRadius:6 }}>
                          ⏳ Comprovante em revisão
                        </span>
                      )}
                      {inv.proof_status === 'rejected' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => openProofModal(inv)} title={inv.proof_review_notes || 'Comprovante anterior rejeitado'}>
                          ⚠️ Reenviar comprovante
                        </button>
                      )}
                      {inv.status==='paid' && <span style={{ fontSize:13, color:'var(--success)', fontWeight:700 }}>✓ Pago</span>}
                      {inv.status==='overdue' && !inv.proof_status && <span style={{ fontSize:13, color:'var(--danger)', fontWeight:700 }}>⚠️ Vencido</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
      )}

      {pixModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setPixModal(null)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h3>Pagar com PIX</h3>
              <button className="modal-close" onClick={() => setPixModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{textAlign:'center',marginBottom:8}}>
                <div style={{fontSize:26,fontWeight:800,color:'var(--gray-900)'}}>{fmt(pixModal.amount)}</div>
                <div style={{fontSize:12,color:'var(--gray-400)',marginTop:4}}>
                  Vencimento: {fmtDate(pixModal.due_date)}
                </div>
                {pixModal.description && <div style={{fontSize:13,color:'var(--gray-600)',marginTop:4}}>{pixModal.description}</div>}
              </div>

              {isPixPayload(pixModal.pix_code) ? (
                <>
                  <PixQRCode code={pixModal.pix_code} />
                  <p style={{ fontSize:12, color:'var(--gray-500)', textAlign:'center', marginBottom:10 }}>
                    Ou copie o código PIX Copia e Cola:
                  </p>
                  <div className="pix-code-box" style={{ fontSize:11, wordBreak:'break-all', marginBottom:12 }}>
                    {pixModal.pix_code}
                  </div>
                  <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }} onClick={handleCopyPix}>
                    {copied ? '✓ Copiado!' : '📋 Copiar código PIX'}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ textAlign:'center', marginBottom:16 }}>
                    <div style={{ fontSize:40, marginBottom:8 }}>🔑</div>
                    <div style={{ fontSize:13, color:'var(--gray-500)' }}>Pague via chave PIX no seu app de banco</div>
                  </div>
                  <label style={{ fontSize:13, fontWeight:600, color:'var(--gray-700)', display:'block', marginBottom:6 }}>Chave PIX</label>
                  <div className="pix-code-box" style={{ fontSize:14, fontWeight:600, wordBreak:'break-all', marginBottom:12, textAlign:'center' }}>
                    {pixModal.pix_code}
                  </div>
                  <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }} onClick={handleCopyPix}>
                    {copied ? '✓ Copiado!' : '📋 Copiar chave PIX'}
                  </button>
                </>
              )}
              {pixModal.pix_identifier && (
                <div style={{ marginTop:14, padding:'10px 12px', background:'var(--gray-50)', border:'1px solid var(--gray-200)', borderRadius:8, fontSize:12 }}>
                  <strong style={{ display:'block', marginBottom:4, color:'var(--gray-700)' }}>💡 Identificador desta fatura</strong>
                  <div style={{ fontFamily:'monospace', fontWeight:700, color:'var(--primary)', fontSize:14 }}>{pixModal.pix_identifier}</div>
                  <div style={{ color:'var(--gray-500)', marginTop:4 }}>
                    Se seu banco pedir descritivo, cole este código. Ajuda a identificar sua fatura no extrato.
                  </div>
                </div>
              )}
              <div className="info-box" style={{ marginTop:14, fontSize:12, textAlign:'center' }}>
                Após pagar, clique em <strong>"Já paguei"</strong> na lista de faturas para enviar o comprovante e acelerar a confirmação.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: envio de comprovante PIX */}
      {proofModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && closeProofModal()}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h3>📎 Enviar comprovante</h3>
              <button className="modal-close" onClick={closeProofModal}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign:'center', marginBottom:14 }}>
                <div style={{ fontSize:20, fontWeight:800, color:'var(--gray-900)' }}>{fmt(proofModal.amount)}</div>
                <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
                  Fatura #{proofModal.id} · Vence {fmtDate(proofModal.due_date)}
                </div>
              </div>

              {!proofResult && (
                <>
                  <p style={{ fontSize:13, color:'var(--gray-600)', marginBottom:12, lineHeight:1.5 }}>
                    Anexe o <strong>comprovante do PIX</strong> que você recebeu do seu banco.
                    Preferimos <strong>PDF</strong> — nosso sistema lê e confirma automaticamente.
                    Imagens (PNG/JPG) também funcionam mas precisam de revisão manual (até 24h).
                  </p>

                  <label style={{
                    display:'block', padding:'20px', borderRadius:10, border:'2px dashed var(--gray-300)',
                    background:'var(--gray-50)', textAlign:'center', cursor:'pointer', transition:'all .15s',
                  }}>
                    <input
                      type="file"
                      accept={PROOF_ACCEPT}
                      onChange={handleProofFileChange}
                      style={{ display:'none' }}
                      disabled={proofSending}
                    />
                    {!proofFile ? (
                      <>
                        <div style={{ fontSize:32, marginBottom:6 }}>📄</div>
                        <div style={{ fontSize:13, fontWeight:600, color:'var(--gray-700)' }}>
                          Clique para escolher o arquivo
                        </div>
                        <div style={{ fontSize:11, color:'var(--gray-500)', marginTop:4 }}>
                          PDF ou imagem · máx {PROOF_MAX_MB} MB
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize:28, marginBottom:6 }}>
                          {proofFile.type === 'application/pdf' ? '📕' : '🖼️'}
                        </div>
                        <div style={{ fontSize:13, fontWeight:600, color:'var(--gray-900)', wordBreak:'break-all' }}>
                          {proofFile.name}
                        </div>
                        <div style={{ fontSize:11, color:'var(--gray-500)', marginTop:4 }}>
                          {(proofFile.size / 1024).toFixed(0)} KB · Clique para trocar
                        </div>
                      </>
                    )}
                  </label>

                  {proofModal.proof_status === 'rejected' && proofModal.proof_review_notes && (
                    <div className="error-box" style={{ marginTop:12, fontSize:12 }}>
                      <strong>Comprovante anterior rejeitado:</strong><br />
                      {proofModal.proof_review_notes}
                    </div>
                  )}
                </>
              )}

              {proofResult && (
                <div className={proofResult.auto_confirmed ? 'success-box' : 'info-box'} style={{ fontSize:13 }}>
                  <div style={{ fontSize:24, textAlign:'center', marginBottom:8 }}>
                    {proofResult.auto_confirmed ? '✅' : '⏳'}
                  </div>
                  <div style={{ fontWeight:600, marginBottom:6 }}>
                    {proofResult.auto_confirmed ? 'Pagamento confirmado!' : 'Comprovante em revisão'}
                  </div>
                  <div style={{ lineHeight:1.5 }}>{proofResult.message}</div>
                  {/* Flags técnicos (e2e_nao_encontrado, valor_divergente etc)
                      NÃO são exibidos pro tenant — são informações internas pra
                      debug. O master vê tudo na tela de revisão de comprovantes. */}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {!proofResult ? (
                <>
                  <button className="btn btn-secondary" onClick={closeProofModal} disabled={proofSending}>Cancelar</button>
                  <button
                    className={`btn btn-primary${proofSending ? ' btn-loading' : ''}`}
                    onClick={handleSubmitProof}
                    disabled={!proofFile || proofSending}
                  >
                    {proofSending ? 'Enviando...' : 'Enviar comprovante'}
                  </button>
                </>
              ) : (
                <button className="btn btn-primary" onClick={closeProofModal}>Fechar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
